import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminTableSkeleton } from "../components/admin/AdminTableSkeleton";
import { ToolCallsTable } from "../components/admin/ToolCallsTable";
import { useAdminUsers } from "../hooks/useAdminUsers";
import {
  useAdminToolCalls,
  type ToolCallStatus,
} from "../hooks/useAdminToolCalls";
import tableStyles from "../components/admin/ToolCallsTable.module.css";
import styles from "./AdminToolCalls.module.css";
import { AdminPageIntro } from "../components/admin/AdminPageIntro";

export const AdminToolCalls = (): JSX.Element => {
  const { users } = useAdminUsers();
  // Seed the customer filter from ?customerEmail= so deep-links (e.g. the
  // per-instance dashboard's "View all in Tool Logs") land pre-filtered.
  const [searchParams] = useSearchParams();
  const [selectedCustomer, setSelectedCustomer] = useState<string>(
    () => searchParams.get("customerEmail") ?? "",
  );
  const [errorsOnly, setErrorsOnly] = useState<boolean>(false);
  const [unconnectedOnly, setUnconnectedOnly] = useState<boolean>(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string>("");

  const filter = useMemo(
    () => ({
      customerEmail: selectedCustomer || undefined,
      status: (errorsOnly ? "error" : undefined) as ToolCallStatus | undefined,
      integrationSlug: selectedIntegration || undefined,
      unconnectedOnly: unconnectedOnly || undefined,
    }),
    [selectedCustomer, errorsOnly, selectedIntegration, unconnectedOnly],
  );

  const {
    status,
    calls,
    error,
    isFetching,
    integrationSlugs,
    page,
    canPrev,
    canNext,
    pageStart,
    pageEnd,
    refresh,
    nextPage,
    prevPage,
  } = useAdminToolCalls(filter);

  // Populate the integration dropdown from the rows we've paged through so far.
  // Not authoritative (an integration with no recent calls won't appear), but
  // cheap and useful — and the set grows as you page.
  const integrationOptions = integrationSlugs;

  const sortedUsers = useMemo(
    () => users.slice().sort((a, b) => a.email.localeCompare(b.email)),
    [users],
  );

  const clearFilters = (): void => {
    setSelectedCustomer("");
    setErrorsOnly(false);
    setUnconnectedOnly(false);
    setSelectedIntegration("");
  };

  const anyFilterActive =
    selectedCustomer !== "" || errorsOnly || unconnectedOnly || selectedIntegration !== "";

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Logs">
        Every tool call through the MCP gateway, newest first. Click a row for its arguments and any batched sub-tools.
      </AdminPageIntro>

      <section className={styles.toolbar}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Customer</span>
          <select
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">All customers</option>
            {sortedUsers.map((u) => (
              <option key={u.email} value={u.email}>
                {u.email}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Integration</span>
          <select
            value={selectedIntegration}
            onChange={(e) => setSelectedIntegration(e.target.value)}
          >
            <option value="">All integrations</option>
            {integrationOptions.map((slug) => (
              <option key={slug} value={slug}>
                {slug}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.chipGroup} role="group" aria-label="Status filter">
          <button
            type="button"
            className={`${styles.chip} ${errorsOnly ? styles.chipActive : ""}`}
            onClick={() => setErrorsOnly((v) => !v)}
          >
            Errors only
          </button>
          <button
            type="button"
            className={`${styles.chip} ${unconnectedOnly ? styles.chipActive : ""}`}
            onClick={() => setUnconnectedOnly((v) => !v)}
            title="Show only calls that hit a toolkit the tenant hasn't connected (rejected locally, not billed)"
          >
            Skipped only
          </button>
        </div>

        <div className={styles.toolbarRight}>
          {anyFilterActive && (
            <button type="button" className={styles.refreshBtn} onClick={clearFilters}>
              Clear filters
            </button>
          )}
          <button type="button" className={styles.refreshBtn} onClick={() => refresh()}>
            Refresh
          </button>
        </div>
      </section>

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["When", "Customer", "Tool", "Integration", "Status", "Sub-tools", "Duration"]}
            tableClassName={tableStyles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.errorPanel}>
            Couldn't load tool calls{error ? `: ${error}` : "."}
          </div>
        )}
        {status === "ready" && calls.length === 0 && (
          <div className={styles.empty}>
            No tool calls match this filter.
          </div>
        )}
        {status === "ready" && calls.length > 0 && (
          <ToolCallsTable
            calls={calls}
            isFetching={isFetching}
            showCustomer
            pager={{
              page,
              canPrev,
              canNext,
              pageStart,
              pageEnd,
              onPrev: prevPage,
              onNext: nextPage,
            }}
          />
        )}
      </section>
    </div>
  );
};
